import {
  StripeProductCardSkeleton,
  StripeProduct as StripeProductComponent,
} from "@beydesign/storybook"
import { Box, useMediaQuery, useTheme } from "@mui/material"
import React, { useCallback, useMemo, useState } from "react"
import { useAppSelector } from "../redux"
import { createCustomerCheckout } from "../services"
import { BillingInterval, StripeProduct } from "../types"
import { getCurrencySymbol } from "../utils/currency"
import {
  calculatePricePerMonth,
  checkIfPlanIsEnterprise,
  checkIfPlanIsMostPopular,
  findPriceByInterval,
  formatPriceAmount,
  getPaymentButtonProps,
  getPlanBubbleData,
  getPlanUpdateStatus,
  getRegionalPrice,
} from "../utils/regional-pricing"
import InfoTooltip from "./ui/infoTooltip"

interface ProductBoxProps {
  product: StripeProduct
  sortedProducts: StripeProduct[]
  sx?: React.CSSProperties
  selectedInterval?: BillingInterval
}

export function ProductBox({
  product: productDetails,
  sx,
  selectedInterval = BillingInterval.MONTH,
  sortedProducts,
}: ProductBoxProps) {
  const [processing, setProcessing] = useState<boolean>(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const user = useAppSelector((state) => state.app.user)

  const price = useMemo(
    () => findPriceByInterval(productDetails.prices, selectedInterval),
    [productDetails.prices, selectedInterval],
  )

  const recurring = price?.recurring
  const regionalPrice = price ? getRegionalPrice(price) : null

  const pricePerMonth = useMemo(
    () => calculatePricePerMonth(regionalPrice, recurring?.interval),
    [regionalPrice, recurring],
  )

  const handleSettingsClick = useCallback(
    async (productId: string) => {
      try {
        setProcessing(true)
        if (!recurring) {
          window.open(
            "https://calendly.com/awais-beyondpresence/sales_discovery_call",
            "_blank",
          )
        } else {
          const returnUrl = window.location.origin
          const planUpdateStatus = getPlanUpdateStatus(
            productId,
            selectedInterval,
            sortedProducts,
          )
          const successUrl = `${returnUrl}${planUpdateStatus ? `?status=${planUpdateStatus}` : ""}`
          const response = await createCustomerCheckout(
            price.id,
            returnUrl,
            successUrl,
            null,
          )
          window.open(response.checkout_url, "_self")
        }
      } catch (error) {
        console.error("Failed to create checkout session:", error)
      } finally {
        setProcessing(false)
      }
    },
    [user, price, recurring],
  )

  const buttonProps = useMemo(() => {
    return getPaymentButtonProps(
      sortedProducts,
      productDetails,
      selectedInterval,
    )
  }, [sortedProducts, productDetails, selectedInterval])

  const isMostPopular = useMemo(
    () => checkIfPlanIsMostPopular(productDetails),
    [productDetails],
  )
  const isEnterprise = useMemo(
    () => checkIfPlanIsEnterprise(productDetails),
    [productDetails],
  )
  const planBubbleData = useMemo(
    () => getPlanBubbleData(productDetails, selectedInterval),
    [productDetails, selectedInterval],
  )

  return (
    <Box
      width={isMobile ? "100%" : "18%"}
      height={"100%"}
      paddingBottom={"var(--spacing-tokens-size-in-px-spacing-spacing-xl)"}
      sx={{ ...sx }}
    >
      <StripeProductComponent
        title={productDetails.name}
        description={productDetails.description}
        price={
          pricePerMonth !== null
            ? `${getCurrencySymbol(regionalPrice?.currency ?? "USD")}${formatPriceAmount(pricePerMonth)}`
            : "Contact Sales"
        }
        duration={"month"}
        features={productDetails.marketing_features.map((e) => e.name)}
        style={{ height: "100%" }}
        isMostPopular={isMostPopular}
        isEnterprisePlan={isEnterprise}
        buttonProps={{
          ...buttonProps,
          onClick: () => handleSettingsClick(productDetails.id),
          loading: processing,
        }}
        bubbleText={planBubbleData?.text}
        bubbleTooltip={
          planBubbleData?.description ? (
            <InfoTooltip placement="bottom" text={planBubbleData.description} />
          ) : undefined
        }
        bubbleTopOffset={"-2%"}
      />
    </Box>
  )
}

export const SkeletonLoader = () => {
  return Array.from({ length: 5 }).map((_, index) => (
    <StripeProductCardSkeleton
      key={index}
      width="100%"
      showMostPopular={Math.random() < 0.5}
      showFeatures={true}
      featuresCount={Math.floor(Math.random() * 4) + 1}
      style={{ height: "100%" }}
      data-testid={`stripe-product-skeleton-${index}`}
    />
  ))
}
